import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import DescountService from '../../services/descount.service';
import { useTranslation } from '../../hooks/useTranslation';

const DescountForm = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    porcentaje: '',
    monto: '',
    status: 'Active',
    start_date: '',
    end_date: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  useEffect(() => {
    if (isEdit) {
      fetchDescount();
    }
  }, [id, isEdit]);

  const fetchDescount = async () => {
    setLoading(true);
    try {
      const response = await DescountService.get(id);
      const descount = response.data?.data;
      setFormData({
        porcentaje: descount.porcentaje || '',
        monto: descount.monto || '',
        status: descount.status || 'Active',
        start_date: descount.start_date ? descount.start_date.slice(0, 10) : '',
        end_date: descount.end_date ? descount.end_date.slice(0, 10) : '',
      });
    } catch (error) {
      console.error('Error loading descount:', error);
      showSnackbar(t('descounts.errorLoadingDescount'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const num = parseFloat(value);
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      // When user sets one field to a value > 0, clear the other so only one is allowed
      if (name === 'porcentaje' && !Number.isNaN(num) && num > 0) {
        next.monto = '';
      } else if (name === 'monto' && !Number.isNaN(num) && num > 0) {
        next.porcentaje = '';
      }
      return next;
    });
  };
  const handleStatusChange = (e) => {
    setFormData(prev => ({ ...prev, status: e.target.value }));
  };

  const handleSubmit = async () => {
    const p = parseFloat(formData.porcentaje);
    const m = parseFloat(formData.monto);
    const hasP = !Number.isNaN(p) && p > 0;
    const hasM = !Number.isNaN(m) && m > 0;

    if (!hasP && !hasM) {
      showSnackbar(t('descounts.oneOfRequired'), 'error');
      return;
    }
    if (hasP && hasM) {
      showSnackbar(t('descounts.onlyOneAllowed'), 'error');
      return;
    }

    try {
      setLoading(true);
      if (isEdit) {
        await DescountService.update(id, formData);
        showSnackbar(t('descounts.updatedSuccess'));
      } else {
        await DescountService.create(formData);
        showSnackbar(t('descounts.createdSuccess'));
      }
      navigate('/descounts');
    } catch (error) {
      const errorMessage = error.response?.data?.message ||
                          (error.response?.data?.errors ?
                            Object.values(error.response.data.errors).flat().join(', ') :
                            t('descounts.errorSaving'));
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => navigate('/descounts')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5">
              {isEdit ? t('descounts.editDescount') : t('descounts.newDescount')}
            </Typography>
          </Box>
        }
        action={
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {isEdit ? t('descounts.updateDescount') : t('descounts.saveDescount')}
          </Button>
        }
      />
      <CardContent>
            <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('descounts.percentage')}
              name="porcentaje"
              type="number"
              value={formData.porcentaje}
              onChange={handleInputChange}
              placeholder="0"
              helperText={t('descounts.percentageOrAmountHint')}
              inputProps={{ min: 0, max: 100, step: '0.01' }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('descounts.amount')}
              name="monto"
              type="number"
              value={formData.monto}
              onChange={handleInputChange}
              placeholder="0"
              helperText={t('descounts.percentageOrAmountHint')}
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('descounts.status')}</InputLabel>
              <Select
                name="status"
                value={formData.status}
                label={t('descounts.status')}
                onChange={handleStatusChange}
              >
                <MenuItem value="Active">{t('descounts.statusActive')}</MenuItem>
                <MenuItem value="Inactive">{t('descounts.statusInactive')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('descounts.startDate')}
              name="start_date"
              type="date"
              value={formData.start_date}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('descounts.endDate')}
              name="end_date"
              type="date"
              value={formData.end_date}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </CardContent>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default DescountForm;
